/**
 * Email-capture lead-magnet block — inserted into `/registration`.
 *
 * Captures cold-ad traffic that isn't ready to pay the $25 yet but
 * gives us their email + UTM context for the 7-day drip. POSTs to
 * `/api/lead-magnet/subscribe`, then optimistically opens the
 * `registration.pdf` so the visitor walks away with the artifact
 * they were promised.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { EnvelopeSimpleIcon as EnvelopeSimple, CheckCircleIcon as CheckCircle, FileArrowDownIcon as FileArrowDown } from "@phosphor-icons/react";

function getUtmParams() {
  const sp = new URLSearchParams(window.location.search);
  return {
    utm_source: sp.get("utm_source") || null,
    utm_medium: sp.get("utm_medium") || null,
    utm_campaign: sp.get("utm_campaign") || null,
  };
}

export default function LeadMagnetBlock({ source = "registration_page" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/lead-magnet/subscribe", {
        email,
        source,
        ...getUtmParams(),
      });
      setDone(true);
      // Open the 1-pager PDF in a new tab — the email is the long-tail
      // nurture, but the visitor wanted the doc *now*.
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/one-pagers/registration.pdf`;
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f] rounded-2xl p-6 md:p-8 text-white" data-testid="lead-magnet-block">
      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold flex items-center gap-1.5">
            <FileArrowDown size={12} weight="duotone" /> Free download
          </p>
          <h3 className="font-spectral text-2xl sm:text-3xl tracking-tight mt-2">
            Get the 1-page Registration guide
          </h3>
          <p className="text-sm text-white/75 mt-2 leading-relaxed">
            Everything the $25 fee unlocks — in one printable PDF you can save, share, or forward to a co-owner. Plus a 4-email walkthrough on getting your dog DNA-verified.
          </p>
        </div>
        <div>
          {done ? (
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 text-center" data-testid="lead-magnet-success">
              <CheckCircle size={28} weight="fill" className="mx-auto text-gold" />
              <p className="mt-2 font-spectral text-lg">Sent — check your inbox.</p>
              <p className="text-xs text-white/70 mt-1">Your PDF should also be opening in a new tab.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-2" data-testid="lead-magnet-form">
              <label htmlFor="lead-email" className="text-xs uppercase tracking-wider text-white/60">Your email</label>
              <div className="flex items-center gap-2">
                <Input
                  id="lead-email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white text-ink"
                  data-testid="lead-magnet-email"
                />
                <Button
                  type="submit"
                  disabled={busy || !email}
                  className="bg-[#C1272D] hover:bg-[#A21F23] text-white font-semibold rounded-full shrink-0"
                  data-testid="lead-magnet-submit"
                >
                  {busy ? "Sending…" : "Get the PDF"}
                </Button>
              </div>
              <p className="text-[11px] text-white/55 flex items-center gap-1 pt-1">
                <EnvelopeSimple size={11} /> No spam. Unsubscribe in one click from any email.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
